import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment/moment';
import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Case } from '../../../../shared/api/hscan/case';
import { genderToString } from '../../../../shared/utils';
import { PresentationalTableProps } from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  LoadingIndicator,
} from '../../common/components/SortableTable/RenderTable';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';
import {
  TableContext,
  TableElementContext,
  TableElementInformationProvider,
  TableInformationProvider,
} from '../ImageList/ImageListTable';

import { MobileImageTable } from './MobileImageTable';

export const ImageListTable = (
  props: Omit<PresentationalTableProps<Case, 'caseId'>, 'identifyKey'>,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.myImageList.table',
  });
  const { t: globalT } = useTranslation();
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(() => createColumnHelper<Case>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.accessor('requestedDate', {
        header: t('header.requestedDate') as string,
        cell: ({ getValue }) => {
          const value = getValue();
          return value
            ? moment(value).format(`${globalT('common.imageDateFormatString')}`)
            : '';
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('acceptedDate', {
        header: t('header.acceptedDate') as string,
        cell: ({ getValue }) => {
          const value = getValue();
          return value
            ? moment(value).format(`${globalT('common.imageDateFormatString')}`)
            : '';
        },
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('institutionName', {
        header: t('header.institutionName') as string,
      }),
      columnHelper.accessor('patientName', {
        header: t('header.patientName') as string,
      }),
      columnHelper.accessor('patientSex', {
        header: t('header.patientSex') as string,
        cell: ({ getValue }) =>
          globalT(`common.gender.${genderToString(getValue())}`),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('birthDate', {
        header: t('header.birthDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('header.studyDate') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('modality', {
        header: t('header.modality') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
        cell: ({ getValue }) => modalityLabel(getValue()),
      }),
      columnHelper.accessor('bodyPart', {
        header: t('header.bodyPart') as string,
        cell: ({ getValue }) => getValue()?.join(', '),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
    ],
    [columnHelper, t, globalT, modalityLabel],
  );

  const windowSize = useContext(WindowSizeContext);

  return (
    <TableInformationProvider
      {...props}
      cellOptions={cellOptions}
      identifyKey="caseId">
      {windowSize === WindowSize.MOBILE ? (
        <MobileImageTable />
      ) : (
        <BasicTable
          tableContext={TableContext}
          elementContext={TableElementContext}
          ElementProvider={TableElementInformationProvider}
          renderLoading={LoadingIndicator}
        />
      )}
    </TableInformationProvider>
  );
};
